import { ref, computed } from "vue";
import { defineStore } from "pinia"; //cspell:ignore pinia // todo global
import buildElement from "../elements/buildElement";
import generateGuid from "@/lib/generateGuid";
// TODO move these?
import { AvailabilityType } from "@cs/AvailabilityType";
import { deleteDraft as deleteDraftFunc, isOfflineAndUsingServiceWorker, isUsingServiceWorker } from "../../lib/pageIndexOperations";
import { fetchXsrf, throwIfNonSuccessStatus } from "@/lib/fetchHelpers.3";
import { combineLatest, from, Observable, BehaviorSubject } from "rxjs";
import { map, first, switchMap } from "rxjs/operators";
import FormDef from "../elements/FormDef";
import FileDef, { InputFormFileItem } from "../elements/FileDef";
import type ForEachDef from "../elements/ForEachDef";
import type CaptchaControlDef from "../elements/CaptchaControlDef";
import { BaseControlDefBase, BaseElementDefBase, IPostSubmitRedirectElement, isIPostSubmitRedirectElement } from "../elements/BaseElementDef";
import { Comment } from "@cs-ts/comment";
import StateFormDef from "../elements/StateFormDef";
import type PageDef from "../elements/PageDef";
import { watch } from "vue";
import { useMainStore } from "../../../mainStore";
import { StandardFormStatus } from "@cs/StandardFormStatus";
import { ForEachDefItem } from "../elements/ForEachDef";
import { FormJson } from "@cs/FormJson";
import { SaveAllAndSubmitResponse } from "@cs/SaveAllAndSubmitResponse";
import { ManualOverrideInvokeResult } from "@cs/ManualOverrideInvokeResult";
import { FormSubmitResultEnum } from "@cs/FormSubmitResultEnum";
import { ReviewDataData } from "@cs/ReviewDataData";
import { AddCommentResponse } from "@cs/AddCommentResponse";
import { SimpleResult } from "@cs/SimpleResult";
import { FormDefinitionFormItem } from "../elements/__DefinitionDataInterface";
import { CalculationContext } from "../elements/__CalculationContextInterface";
import { KnownFeatureFlags } from "@cs/KnownFeatureFlags";
// TODO separate file?
export function toObserverComputed<T>(func: () => T, log?: string | ((i: T) => string)): Observable<T> {
    const subject = new BehaviorSubject<T>(null!);
    watch(
        func,
        result => {
            if (log) {
                console.log({ log: log instanceof Function ? log(result) : log, result });
            }
            subject.next(result);
        },
        {
            immediate: true,
            onTrack:
                log != null
                    ? result => {
                          console.log({ onTrack: result });
                      }
                    : undefined,
            onTrigger:
                log != null
                    ? result => {
                          console.log({ onTrigger: result });
                      }
                    : undefined,
        },
    );
    return subject;
}
export const usePageStore = defineStore("page", () => {
    const state = {
        visibleFlag: ref(true),
        segment: ref(null as string | null),
        page: ref(null as null | number),
        formData: ref<FormDef | null>(null as null | FormDef),
        baseFormData: ref<FormDef | null>(null as null | FormDef),
        formDataLoading: ref(null as null | boolean),
        adminPreview: ref(null as null | boolean),
        adminReview: ref(null as null | boolean),
        testMode: ref(null as null | boolean),
        adminEditElementIds: ref([] as string[]), // type?
        canAddComments: ref(false as boolean),
        canChangeStatus: ref(false as boolean),
        canUpgradeDefinition: ref(false as boolean),
        canReplyToComments: ref(false as boolean),
        canAdminEditData: ref(false as boolean),
        anonymousUser: ref(false as boolean),
        canDeleteSubmissions: ref(false as boolean),
        canDeleteReviewComments: ref(false as boolean),
        showAdminDiff: ref(false as boolean),
        unsavedFormId: ref(null as string | null),
        //eslint-disable-next-line @typescript-eslint/no-explicit-any
        pendingActions: ref(new Set<Promise<any>>()),
        isSaveDisabledOnSectionChange: ref(false as boolean),
        schemaLabelMappingPromise: ref(null as null | Promise<Record<string, string>>),
        fakeFormId: ref(null as null | string),
        pendingAddressVerifications: ref(new Set<Promise<void>>()),
    } as const;

    function _getElementByVmId(vmId: string | { vmId: string } | string) {
        //@ts-expect-error
        let result = state.formData.value ? (state.formData.value.getElementByVmId(vmId.vmId || vmId) as BaseElementDefBase) : null;
        if (result == null && state.adminPreview.value) {
            //@ts-expect-error
            result = state.baseFormData.value ? state.baseFormData.value.getElementByVmId(vmId.vmId || vmId) : null;
        }
        return result;
    }
    function _getChildren(baseElement: BaseElementDefBase | null | undefined, currentElement: BaseElementDefBase) {
        if (state.adminPreview.value) {
            const baseChildren = baseElement == null ? [] : baseElement.children;
            const currentChildren: BaseElementDefBase[] = currentElement == null ? [] : currentElement.children;
            const result = [];
            let index = 0;
            const maxLength = Math.max(currentChildren.length, baseChildren.length);
            for (index = 0; index < maxLength; index++) {
                if (index < currentChildren.length) {
                    const currentChild = currentChildren[index];
                    result.push(currentChild);
                }
                if (index < baseChildren.length) {
                    const baseChild = baseChildren[index];
                    if (!currentChildren.some(i => i.id == baseChild.id)) {
                        result.push(baseChild);
                    }
                }
            }
            return result;
        } else {
            return currentElement ? currentElement.children : null;
        }
    }
    function _calculateElementPath(element: BaseElementDefBase): string {
        let elementPath = "";
        const rootElement = state.formData.value as FormDef;
        //if (rootElement != element) {
        const elementPathElements = rootElement.getElementPathByVmId(element.vmId) as BaseElementDefBase[];
        //@ts-ignore
        ({ elementPath } = elementPathElements.slice(1, 1000000).reduce(
            //@ts-ignore
            (a, i) => {
                return { parent: i, elementPath: a.elementPath + ".Children[" + a.parent.children.indexOf(i) + "]" };
            },
            { parent: rootElement, elementPath: "FormModel" },
        ));
        return elementPath;
    }
    function _adminPreview() {
        return state.adminPreview.value;
    }
    function _showChanges() {
        return _adminPreview() && state.showAdminDiff.value;
    }
    function _getElementsByFilter(filter: (i: BaseElementDefBase) => boolean) {
        const rootElement = state.formData.value;
        return rootElement!.getElementsByFilter(filter);
    }

    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    const memorizeState = {} as { [a: string]: any };
    function memorizeInState<T>(key: string, func: () => T): T {
        if (!(key in memorizeState)) {
            memorizeState[key] = func();
        }
        return memorizeState[key];
    }

    const pages$ = toObserverComputed(() => _getPages());
    const status$ = toObserverComputed(() => state.formData.value?.status ?? null);
    const segment$ = toObserverComputed(() => state.segment.value);

    function _getVisiblePages$() {
        return memorizeInState("__getVisiblePages$", () => {
            return combineLatest([pages$, status$, segment$]).pipe(
                map(([pages, status, segment]) => {
                    if (pages == null) {
                        return [];
                    }
                    if (state.adminPreview.value) {
                        return pages;
                    }
                    const mode = state.adminReview.value ? "Admin" : "Public"; // TODO?
                    return pages
                        .filter(i => i.isVisible(mode, status!, segment!, state.visibleFlag.value))
                        .sort((a, b) => {
                            const aE = a.isEditable(mode, status!, segment!, state.visibleFlag.value);
                            const bE = b.isEditable(mode, status!, segment!, state.visibleFlag.value);
                            if (aE == bE) {
                                const aS = a.definition.settings.segment;
                                const bS = b.definition.settings.segment;
                                if (aS == bS) {
                                    return 0;
                                } else if (aS! < bS!) {
                                    return -1;
                                } else {
                                    return 1;
                                }
                            } else if (aE) {
                                return 1;
                            } else {
                                return -1;
                            }
                        });
                }),
            );
        });
    }
    function _getVisiblePages() {
        const pages = _getPages() || [];
        if (state.adminPreview.value) {
            return pages;
        }
        return pages
            .filter(i => i.isVisible(state.adminReview.value ? "Admin" : "Public", state.formData.value!.status, state.segment.value!, state.visibleFlag.value))
            .sort((a, b) => {
                const aE = a.isEditable(state.adminReview.value ? "Admin" : "Public", state.formData.value!.status, state.segment.value!, state.visibleFlag.value);
                const bE = b.isEditable(state.adminReview.value ? "Admin" : "Public", state.formData.value!.status, state.segment.value!, state.visibleFlag.value);
                if (aE == bE) {
                    const aS = a.definition.settings.segment;
                    const bS = b.definition.settings.segment;
                    if (aS == bS) {
                        return 0;
                    } else if (aS! < bS!) {
                        return -1;
                    } else {
                        return 1;
                    }
                } else if (aE) {
                    return 1;
                } else {
                    return -1;
                }
            });
    }
    function _getEnabledPages$() {
        return memorizeInState("_getEnabledPages$", () => {
            const subject = _getVisiblePages$();
            return subject.pipe(
                switchMap(j =>
                    combineLatest(
                        (j || []).map(i => {
                            //@ts-ignore
                            const enabled$ = i.isEnabled$ ? i.isEnabled$("Edit") : from([!i.isEnabled || i.isEnabled("Edit")]);
                            const permitLaterPages$ = i.permitLaterPages$ ? i.permitLaterPages$() : from([true]);
                            return combineLatest([enabled$, permitLaterPages$]).pipe(map(([v, l]) => [i, v, l, i.definition.settings.segment ?? ""] as const));
                        }),
                    ),
                ),
                map(i => {
                    const r = i.filter(j => j[1]);
                    const r2 = [];
                    const stop: Record<string, boolean> = {};
                    for (const page of r) {
                        if (stop[page[3]]) {
                            continue;
                        }
                        if (!page[2]) {
                            stop[page[3]] = true;
                        }
                        r2.push(page[0]);
                    }
                    return r2;
                }),
            );
        });
    }
    function _getEndUserStatuses() {
        // TODO maybe this belongs in the FormDef class
        // TODO: Proper annotation
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (state.formData.value!.definition as any).effectiveStatuses.filter((i: any) => i.mode == "User").map((i: any) => i.value);
    }
    function _getPages(): PageDef[] {
        return (_getChildren(state.baseFormData.value as FormDef, state.formData.value! as FormDef) || []).filter(i => i.definition.type == "PageDef").filter(i => (_showChanges() ? true : i.isBase !== true)) as unknown as PageDef[];
    }
    function segmentsF() {
        if (state.formData.value == null) {
            return null;
        }
        return (state.formData.value.getElementsByFilter((i: BaseElementDefBase) => i.definition.type == "StateFormDef").slice() as StateFormDef[]).sort((a, b) => (a.completedDate < b.completedDate ? -1 : a.completedDate > b.completedDate ? 1 : 0));
    }
    function _initializedSegments() {
        const segments = segmentsF();
        if (segments != null && segments.length > 0) {
            if (_adminPreview()) {
                return segments;
            }
            const matchingSegments = segments.filter(i => i.activatedDate || i.completedDate);
            return matchingSegments;
        }
        return null;
    }
    function _uninitializedSegments() {
        const segments = segmentsF();
        if (segments != null && segments.length > 0) {
            if (_adminPreview()) {
                return segments;
            }
            const matchingSegments = segments.filter(i => !i.activatedDate && !i.completedDate);
            return matchingSegments;
        }
        return null;
    }
    function _completedSegments() {
        const segments = segmentsF();
        if (segments != null && segments.length > 0) {
            const matchingSegments = segments.filter(i => i.completedDate != null && i.status != "Returned" && i.state != "ReturnedPending");
            return matchingSegments;
        }
        return null;
    }
    function _visibleSegments() {
        const segments = segmentsF();
        if (segments != null && segments.length > 0) {
            if (_adminPreview()) {
                return segments;
            }
            const matchingSegments = segments.filter(i => i.can("view", state.adminReview.value ?? false));
            return matchingSegments;
        }
        return null;
    }
    function _status() {
        return state.formData.value ? state.formData.value.status : null;
    }
    function _unlockableSegments() {
        const segments = _visibleSegments();
        if (segments != null) {
            if (_adminPreview()) {
                return segments;
            }
            const matchingSegments = segments.filter(i => i.can("unlock", state.adminReview.value ?? false));
            return matchingSegments;
        }
        return null;
    }
    function _availableSegments() {
        const segments = _visibleSegments();
        if (segments != null) {
            if (_adminPreview()) {
                return segments;
            }
            const matchingSegments = segments.filter(i => i.can("assign", state.adminReview.value ?? false));
            return matchingSegments;
        }
        return null;
    }

    function _activeSegments() {
        const segments = _availableSegments();
        if (segments != null) {
            if (_adminPreview()) {
                return segments;
            }
            const matchingSegments = segments.filter(i => i.can("edit", state.adminReview.value ?? false));
            return matchingSegments;
        }
        return null;
    }

    function _hasCompletedSegmentsToReturnTo() {
        const segments = _completedSegments();
        if (isWorkflow.value) {
            if (segments != null && segments.length > 0 && segments.some(x => x.definition?.settings?.allowReturning == true || x.definition?.settings?.allowReturning == null)) {
                return true;
            }
            return false;
        }
        return true;
    }
    function _possibleStagesToReturn() {
        const segments = _completedSegments();
        if (isWorkflow.value) {
            return segments?.filter(i => i.definition?.settings?.allowReturning == true || i.definition?.settings?.allowReturning == null);
        }
        return segments;
    }
    const getElementByVmId = (vmId: BaseElementDefBase | string) => _getElementByVmId(vmId);
    const getElementsById$ = (id: string, contextVmId: string) => (state.formData.value ? state.formData.value.getElementsById$(id, contextVmId) : from([[]]));
    const formDataTyped = computed(() => {
        return state.formData.value;
    });
    const baseFormDataTyped = computed(() => {
        return state.baseFormData.value;
    });
    const getValueByVmId = (vmId: string | BaseElementDefBase) => {
        const element = getElementByVmId(vmId) as BaseControlDefBase | null;
        if (element == null) {
            return null;
        }
        return element.value();
    };
    const getChildrenByVmId = (vmId: string | BaseElementDefBase) => {
        const element = getElementByVmId(vmId)!;
        const currentElement = element.isBase ? element.baseLink! : element;
        const baseElement = element.isBase ? element : element.baseLink;
        return _getChildren(baseElement, currentElement);
    };
    const isAdminEditingById = (vmId: string) => {
        return state.adminEditElementIds.value.includes(vmId);
    };
    const getValuesById$ = (id: string, contextVmId: string) => {
        const elements = getElementsById$(id, contextVmId);
        return elements.pipe(switchMap((i: BaseElementDefBase[] | null) => (i == null ? from([null]) : combineLatest(i.map(j => (j as BaseControlDefBase).effectiveValueList$())).pipe(map(valueLists => Array.prototype.concat.apply([], valueLists))))));
    };
    const getElementPathString = (element: BaseElementDefBase) => _calculateElementPath(element);
    const viewTypes = computed(() => {
        return state.formData.value!.viewTypes;
    });
    const directLinkTemplates = computed(() => {
        return _getElementsByFilter(i => i.definition.type == "DirectLinkTemplateDef");
    });

    const isWorkflow = computed(() => {
        if (state.formData.value == null) {
            return null;
        }
        return state.formData.value.getElementsByFilter((i: BaseElementDefBase) => i.definition.type == "WorkflowDef").length > 0;
    });
    const allFileElements = computed(() => {
        return _getElementsByFilter(i => i.definition.type == "FileDef");
    });
    const allElementsWithFileComments = computed(() => {
        return _getElementsByFilter(i => i.comments != null && i.comments.some(i => i.files != null && i.files.length > 0));
    });
    const allElementsWithHistory = computed(() => {
        return _getElementsByFilter(i => i.history != null && i.history.length > 0);
    });
    const allHistoryEntries = computed(() => {
        return allElementsWithHistory.value.map(e => e.history.map(h => ({ ...h, element: e }))).flat();
    });
    const allElementsWithIssues = computed(() => {
        return _getElementsByFilter(i => i.issues != null && i.issues.length > 0);
    });
    const allIssuesEntries = computed(() => {
        return allElementsWithIssues.value.map(e => e.issues.map(h => ({ ...h, element: e }))).flat();
    });
    const hasIssues = computed(() => {
        if (state.formData.value == null) {
            return null;
        }
        return allElementsWithIssues.value.length > 0;
    });
    const segments = computed(() => {
        return segmentsF();
    });
    const initializedSegments = computed(() => {
        return _initializedSegments();
    });
    const uninitializedSegments = computed(() => {
        return _uninitializedSegments();
    });
    const visibleSegments = computed(() => {
        return _visibleSegments();
    });
    const availableSegments = computed(() => {
        return _availableSegments();
    });
    const unlockableSegments = computed(() => {
        return _unlockableSegments();
    });
    const completedSegments = computed(() => {
        return _completedSegments();
    });
    const activeSegments = computed(() => {
        return _activeSegments();
    });
    const hasCompletedSegmentsToReturnTo = computed(() => {
        return _hasCompletedSegmentsToReturnTo();
    });
    const possibleStagesToReturn = computed(() => {
        return _possibleStagesToReturn();
    });
    //const state.adminPreview = computed(() => {
    //    return _adminPreview(stateB);
    //},
    //state.adminReview(stateB)["adminReview"] {
    //    return adminReview.value;
    //},
    //testMode(stateB)["testMode"] {
    //    return testMode.value;
    //},
    //canAddComments(stateB)["canAddComments"] {
    //    return canAddComments.value;
    //},
    //canChangeStatus(stateB)["canChangeStatus"] {
    //    return canChangeStatus.value;
    //},
    //canReplyToComments(stateB)["canReplyToComments"] {
    //    return canReplyToComments.value;
    //},
    //anonymousUser(stateB)["anonymousUser"] {
    //    return anonymousUser.value;
    //},
    const showChanges = computed(() => {
        return _showChanges();
    });
    const hasChanges = computed(() => {
        return state.adminPreview.value && state.formData.value != null ? state.formData.value.baseChangeRecursive() == "changed" : false;
    });
    const isSubmittable$ = computed(() => {
        return pages$.pipe(
            switchMap(i => combineLatest(i!.map(j => j.permitLaterPages$()))),
            map(i => i.every(j => j)),
        );
    });
    const isOffline = computed(() => {
        if (state.formData.value != null) {
            if (state.adminPreview.value || state.adminReview.value) {
                return false;
            }
            if (status.value == StandardFormStatus.Returned) {
                return false;
            }
            const defSettings = state.formData.value.definition.settings;
            const availability = defSettings.availability;
            if (availability == AvailabilityType.Online) {
                return false;
            } else if (availability == AvailabilityType.Offline) {
                return true;
            } else if (availability == AvailabilityType.Scheduled || availability == null) {
                const now = new Date();
                const zonedToJsTime = (date: string) => new Date(date.replace(/\s.*$/, "").replace(/([-+]\d\d)$/, "$1:00"));
                const startDate = defSettings.effectiveStartDate;
                const endDate = defSettings.effectiveDeadline;
                return (startDate != null && now < zonedToJsTime(startDate)) || (endDate != null && now >= zonedToJsTime(endDate));
            } else {
                debugger;
                return false;
            }
        } else {
            return false;
        }
    });
    const endUserStatuses = computed(() => {
        return _getEndUserStatuses();
    });
    const formId = computed(() => {
        return state.formData.value ? state.formData.value.formId : null;
    });
    const formTenantCode = computed(() => {
        return state.formData.value ? state.formData.value.definition.formTenant : null;
    });
    const status = computed(() => {
        return _status();
    });
    const title = computed(() => {
        return state.formData.value ? state.formData.value.definition.settings.label : null;
    });
    const offlineContent = computed(() => {
        return state.formData.value ? state.formData.value.definition.settings.offlineContent : null;
    });
    const mustLogin = computed(() => {
        return state.formData.value ? state.formData.value.definition.settings.mustLogin || (activeSegments.value || []).some(i => i.definition.settings.mustLogin) : null;
    });
    const formSettings = computed(() => {
        return state.formData.value ? state.formData.value.definition.settings : null;
    });
    const isViewOnly = computed(() => {
        return state.formData.value ? !!state.formData.value.definition.settings.isViewOnly : null;
    });
    const isPageViewOnly = (pageNumber: number) => {
        const page = pages.value[pageNumber - 1];
        return isPageObjViewOnly(page, false);
    };
    const isPageEditable = (pageNumber: number) => {
        const page = pages.value[pageNumber - 1];
        return isPageObjEditable(page);
    };
    const isPageObjViewOnly$ = (page: PageDef) => {
        if (isViewOnly.value) {
            return from([true as null | boolean]);
        }
        return isPageObjControlsViewOnly$(page);
    };
    const isPageObjControlsViewOnly$ = (page: PageDef) => {
        if (page == null) {
            return from([null as null | boolean]);
        }
        if (state.adminPreview.value) {
            return from([false as null | boolean]);
        }
        return combineLatest([status$, segment$]).pipe(
            map(([status, segment]) => {
                if (page.isViewOnly(state.adminReview.value ? "Admin" : "Public", status!, segment!, state.visibleFlag.value)) {
                    return true;
                }
                return null;
            }),
        );
    };
    const isPageObjViewOnly = (page: PageDef, ignoreSegment: boolean) => {
        if (isViewOnly.value) {
            return true;
        }
        if (page == null) {
            return null;
        }
        if (state.adminPreview.value) {
            return false;
        }
        if (page.isViewOnly(state.adminReview.value ? "Admin" : "Public", status.value!, ignoreSegment ? null : state.segment.value!, state.visibleFlag.value)) {
            return true;
        }
        return null;
    };
    const isPageObjEditable = (page: PageDef) => {
        if (isViewOnly.value) {
            return false;
        }
        if (page == null) {
            return null;
        }
        if (state.adminPreview.value) {
            return true;
        }
        if (page.isEditable(state.adminReview.value ? "Admin" : "Public", status.value!, state.segment.value!, state.visibleFlag.value)) {
            return true;
        }
        return null;
    };
    const pages = computed(() => {
        return _getPages();
    });
    const currentStatusLabel = computed(() => {
        if (state.formData.value) {
            const status = state.formData.value!.definition.effectiveStatuses!.find(i => i.value == state.formData.value!.status)!;
            return status.adminText || status.value;
        }
        return null;
    });
    const statusLabel = (status: string) => {
        if (state.formData.value) {
            const status2 = state.formData.value.definition.effectiveStatuses?.find(i => i.value == status);
            return status2?.adminText || status2?.value;
        }
        return null;
    };
    const isClientValidationOnly = computed(() => {
        return (_getChildren(state.baseFormData.value as unknown as BaseElementDefBase, state.formData.value! as unknown as BaseElementDefBase) || []).filter(i => (_showChanges() ? true : i.isBase !== true)).some(i => i.definition.type == "DynamicContentTemplateDef");
    });
    const possibleStatuses = computed(() => {
        return state.formData.value ? state.formData.value.definition.effectiveStatuses?.filter(i => i.value != StandardFormStatus.Deleted && i.value != state.formData.value!.status && !i.value?.includes("(submitting)") && !i.adminText?.includes("(submitting)")) ?? [] : [];
    });
    const visiblePages = computed(() => {
        return _getVisiblePages();
    });
    const visiblePages$ = computed(() => {
        return _getVisiblePages$();
    });
    const enabledPages$ = computed(() => {
        return _getEnabledPages$();
    });
    const enabledPagesPromise = computed(() => {
        return enabledPages$.value.pipe(first()).toPromise();
    });
    //unenabledPages: (state ,getters) => {
    //    const pages = getters['pages']||[];
    //    const enabledPages = new Set(getters['enabledPages']||[]);
    //    return pages.filter(i => !enabledPages.has(i));
    //},
    const comments = computed(() => {
        return state.formData.value ? state.formData.value.comments : null;
    });
    const pendingActionPromise = computed(() => {
        return Promise.all(Array.from(state.pendingActions.value));
    });
    const setAdminEditing = ({ vmId, enabled }: { vmId: string; enabled: boolean }): void => {
        if (enabled) {
            if (!state.adminEditElementIds.value.includes(vmId)) {
                state.adminEditElementIds.value.push(vmId);
            }
        } else {
            if (state.adminEditElementIds.value.includes(vmId)) {
                state.adminEditElementIds.value = state.adminEditElementIds.value.filter(i => i != vmId);
            }
        }
    };
    const clearAdminEditing = (): void => {
        state.adminEditElementIds.value = [];
    };
    const setSegment = (segmentI: string): void => {
        state.segment.value = segmentI;
    };
    const setValueByVmId = (params: { id: string; value: string | string[] }): void => {
        (_getElementByVmId(params.id) as BaseControlDefBase).setValue(params.value);
    };
    const setFileStatusByVmId = (params: { id: string; status: string }): void => {
        const element = _getElementByVmId(params.id) as FileDef;
        element.uploadStatus = params.status;
    };
    const setFileInfoByVmId = (params: Partial<InputFormFileItem> & { id: string }): void => {
        const element = _getElementByVmId(params.id) as FileDef;
        element.setFileData(params as InputFormFileItem);
    };
    const addForEachItem = (vmId: string): void => {
        (_getElementByVmId(vmId) as ForEachDef)?.addItem(null);
    };
    const removeForEachItem = (params: { forEachVmId: string; item: ForEachDefItem; calculatedItems: string[] }): void => {
        (_getElementByVmId(params.forEachVmId) as ForEachDef)?.removeItem(params.item, params.calculatedItems);
    };
    const setForEachChildren = (params: { forEachVmId: string; ids: string[] }): void => {
        const foreach = _getElementByVmId(params.forEachVmId) as unknown as ForEachDef;
        if (foreach != null) {
            foreach.setCalculationItems(params.ids);
        }
    };
    const toggleAdminDiff = (): void => {
        state.showAdminDiff.value = !state.showAdminDiff.value;
    };
    //setFormSubmitButtonText = (value: string): void => {
    //    state.formData.value!.definition.settings.effectiveSubmitButtonText = value;
    //},
    const _formDataIsLoading = (): void => {
        state.formDataLoading.value = true;
        state.formData.value = null;
    };
    // TODO: Proper annotation
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const _formDataLoaded = (data: any): void => {
        state.fakeFormId.value = null;
        state.unsavedFormId.value = null;
        state.segment.value = data.segment;
        state.formData.value = data.formData;
        state.baseFormData.value = data.baseFormData;
        state.formDataLoading.value = false;
        state.adminPreview.value = data.adminPreview;
        state.adminReview.value = data.adminReview;
        state.testMode.value = data.testMode;
        state.canAddComments.value = data.canAddComments;
        state.canChangeStatus.value = data.canChangeStatus;
        state.canUpgradeDefinition.value = data.canUpgradeDefinition;
        state.canReplyToComments.value = data.canReplyToComments;
        state.canAdminEditData.value = data.canAdminEditData;
        state.anonymousUser.value = data.anonymousUser;
        state.canDeleteSubmissions.value = data.canDeleteSubmissions;
        state.canDeleteReviewComments.value = data.canDeleteReviewComments;
        state.adminEditElementIds.value = [];
    };
    const _formDataUpdated = (formDataI: FormJson): void => {
        state.formData.value!.setData(formDataI);
    };
    const _setFormId = (formId: string): void => {
        state.formData.value!.formId = formId;
        state.unsavedFormId.value = null;
    };
    const _setAllPagesStatus = (status: string | null): void => {
        _getVisiblePages().forEach(p => {
            if (p.setStatus) {
                p.setStatus(status!);
            }
        });
    };
    const _addPendingAction = <T>(promise: Promise<T>): void => {
        state.pendingActions.value.add(promise);
    };
    const _removePendingAction = <T>(promise: Promise<T>): void => {
        state.pendingActions.value.delete(promise);
    };
    const _addComment = ({ elementData, comment }: { elementData: BaseElementDefBase; comment: Comment }): void => {
        //@ts-ignore
        elementData.comments!.push(comment);
    };
    const _setCommentVisibility = ({ elementData, commentId, visibleToSubmitter }: { elementData: BaseElementDefBase; commentId: string; visibleToSubmitter: boolean }): void => {
        const comment = elementData.comments.find(i => i.id == commentId);
        comment!.visibleToSubmitter = visibleToSubmitter;
    };
    const _makeUneditable = (): void => {
        state.canAddComments.value = false;
        state.canChangeStatus.value = false;
        state.canReplyToComments.value = false;
        state.canAdminEditData.value = false;
        state.anonymousUser.value = false;
    };
    const _setSchemaLabelMapping = (value: Promise<Record<string, string>> | null): void => {
        state.schemaLabelMappingPromise.value = value;
    };
    const _trackAction = <T>(promise: Promise<T>): Promise<T> => {
        _addPendingAction(promise);
        // TODO: Proper annotation
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const remove = (r: T) => {
            _removePendingAction(promise);
            return r;
        };
        promise.then(remove, remove);
        return promise;
    };
    // TODO: Proper annotation
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const _formPageDataUpdated = async (params: any) => {
        if (params.page == null) {
            state.formData.value!.setData(params.data, false);
        } else if (params.page >= 0) {
            state.formData.value!.setData(params.data, true);
            pages.value[params.page - 1].setData(params.data.children[params.childIndex]);
        }
        if (segments.value?.length ?? 0 > 0) {
            const availableSegmentsI = availableSegments.value!;
            if (!availableSegmentsI.some(i => i.id == state.segment.value)) {
                const selectedSegment = availableSegmentsI.length == 1 ? availableSegmentsI[0].id! : state.adminPreview.value && availableSegmentsI.length > 0 ? availableSegmentsI[0].id! : "NOMATCH";
                setSegment(selectedSegment);
            }
        }
    };
    // TODO: Proper annotation
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const _setPageStatus = async (params: any) => {
        pages.value[params.page - 1].setStatus(params.status);
    };
    let formIdPromise: Promise<unknown> | null = null;
    const getOrCreateFormId = async (): Promise<string> => {
        if (isViewOnly.value || state.adminPreview.value) {
            if (state.fakeFormId.value == null) {
                state.fakeFormId.value = generateGuid();
            }
            return state.fakeFormId.value;
        } else {
            while (formId.value == null) {
                if (!formIdPromise) {
                    formIdPromise = saveFormPage({ page: -1 });
                }
                await formIdPromise;
                formIdPromise = null;
                // Adding delay to process the response and update formId.value in background
                await new Promise(callback => setTimeout(callback, 0));
            }
            return formId.value!;
        }
    };
    const uploadExternalFile = async (params: { newFile: File; vmId: string; isComment?: boolean }): Promise<{ valueId: string; isThreat: boolean; status: string } | undefined> => {
        return _trackAction(
            (async () => {
                const { newFile, vmId, isComment } = params;
                let formIdI = formId.value;
                if (formIdI == null) {
                    await saveFormPage({ page: -1 });
                    formIdI = formId.value;
                }
                const valueId = generateGuid();
                const uploadData = new FormData();
                let status = "";
                let isThreat = false;
                uploadData.append("formID", formIdI ?? "");
                uploadData.append("asAdmin", (state.adminReview.value ?? false).toString());
                uploadData.append("testMode", (state.testMode.value ?? false).toString());
                uploadData.append("valueID", valueId);
                uploadData.append("formFile", newFile, newFile.name);
                if (isComment) {
                    // TODO?
                } else {
                    status = "in-progress";
                }

                const mainStore = useMainStore();
                const uploadUrl = await mainStore.getUrl("uploadExternalReference", { areaCode: formTenantCode.value });
                const successCallback = () => {
                    if (isComment) {
                        // TODO?
                    } else {
                        status = "ok";
                        setFileInfoByVmId({
                            id: vmId,
                            valueId: valueId,
                            contentType: newFile.type,
                            fileName: newFile.name,
                            fileSize: newFile.size,
                            hasValue: true,
                        });
                    }
                };
                const failCallback = (tooLarge?: boolean) => {
                    if (isComment) {
                        // TODO?
                    } else {
                        status = tooLarge ? "too-large" : "fail";
                    }
                };
                const avScanFailureCallback = () => {
                    if (isComment) {
                        isThreat = true;
                    } else {
                        status = "avScanFail";
                    }
                };
                const offlineUpload = () => {
                    // TODO only on connection failure?
                    const filereader = new FileReader();
                    filereader.readAsText(newFile);
                    filereader.addEventListener("load", async () => {
                        try {
                            const uploadOfflineUrl = uploadUrl.replace("UploadExternalReference", "ServiceWorkerOfflineUploadReference"); // TODO await .value$root.getUrl('uploadOfflineExternalReference');
                            const uploadData = {
                                formId: formId,
                                valueId: valueId,
                                fileName: newFile.name,
                                mimeType: newFile.type,
                                data: filereader.result,
                            };
                            const offlineUploadResponse = await fetchXsrf(mainStore, uploadOfflineUrl, {
                                method: "POST",
                                headers: {
                                    "Content-Type": "application/json",
                                },
                                credentials: "same-origin",
                                body: JSON.stringify(uploadData),
                            });
                            if (offlineUploadResponse.status == 200) {
                                successCallback();
                            } else {
                                failCallback();
                            }
                        } catch (err) {
                            failCallback();
                            throw err;
                        }
                    });
                    // TODO
                    //@ts-expect-error
                    filereader.addEventListener("abort", failCallback);
                    //@ts-expect-error
                    filereader.addEventListener("error", failCallback);
                };
                let uploadResponse;
                try {
                    uploadResponse = await fetchXsrf(mainStore, uploadUrl, {
                        method: "POST",
                        credentials: "same-origin",
                        body: uploadData,
                    });
                } catch (err) {
                    if (isUsingServiceWorker()) {
                        await offlineUpload();
                    } else {
                        failCallback();
                        throw err;
                    }
                    return;
                }
                if (uploadResponse.status == 200) {
                    const resp = await uploadResponse.json();
                    if (resp?.ThreatDetected == true) {
                        avScanFailureCallback();
                    } else {
                        successCallback();
                    }
                } else if (isOfflineAndUsingServiceWorker(uploadResponse)) {
                    await offlineUpload();
                } else {
                    failCallback(uploadResponse.status == 413);
                }
                return { valueId, isThreat, status };
            })(),
        );
    };
    const submitForm = async (triggerKey: string | null): Promise<null | SaveAllAndSubmitResponse> => {
        // TODO
        const buildOfflineResponse = async (form: FormDef) => {
            // TODO: Proper annotation
            const redirectUrls = (await Promise.all(form.getElementsByFilter(i => isIPostSubmitRedirectElement(i)).map(item => (item as unknown as IPostSubmitRedirectElement).getPostSubmitRedirect()))).filter(i => i != null);
            const formValid = await form.valid$(true);
            await new Promise(r => setTimeout(r, 0));
            return {
                Result: formValid.pipe(first()) ? (redirectUrls.length > 0 ? FormSubmitResultEnum.SubmittedWithRedirect : FormSubmitResultEnum.Submitted) : FormSubmitResultEnum.ValidationFailed,
                RedirectUrl: redirectUrls.length > 0 ? redirectUrls[0] : undefined,
            };
        };
        if (isViewOnly.value) {
            throw new Error("Readonly");
        }
        if (state.adminPreview.value) {
            (state.formData.value as FormDef).markEdited();
            return await buildOfflineResponse(state.formData.value! as FormDef);
        }
        const result = (await saveFormPage({ page: null, isSubmit: true, triggerKey })) as SaveAllAndSubmitResponse;
        if (result != null) {
            return result;
        } else if (isUsingServiceWorker()) {
            return await buildOfflineResponse(state.formData.value! as FormDef);
        } else {
            return null;
        }
        //if (response.status == 200) {
        //    return await response.json();
        //} else if (isOfflineAndUsingServiceWorker(response)) {
        //    return await buildOfflineResponse(form);
        //} else {
        //    throw new Error("Not 200 OK: "+response.status + " " +response.statusText)
        //}
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const selfAssignSegment = async ({ segment, verifyData }: { segment: string; verifyData: any }): Promise<string[]> => {
        const result = await invokeForm({ type: "SelfAssign", target: segment, segment, data: verifyData, assign: true });
        if (result.Success) {
            setSegment(segment);
        }
        return result.FailureMessages;
    };
    const saveAllAdminEdits = async () => {
        const changes = [];
        for (const vmId of state.adminEditElementIds.value) {
            const element = _getElementByVmId(vmId)!;
            const path = getElementPathString(element);
            const data = element.getData();
            changes.push({ type: "AdminEdit", target: /* TODO not unique - ForEach */ element.id!, targetPath: path, data: data });
        }
        // Delay so new values aren't overwritten by invoke response
        for (const change of changes) {
            await invokeForm(change);
        }
        clearAdminEditing();
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const invokeForm = ({ segment, type, target, targetPath, targetPathArray, data, assign, noWrite, unlock }: { segment?: string | null; type: string; target: string; targetPath?: string; targetPathArray?: number[]; data?: any; assign?: boolean; noWrite?: boolean; unlock?: boolean }): Promise<any> => {
        return _trackAction(
            (async () => {
                if (state.adminPreview.value) {
                    throw new Error("Admin preview");
                }
                if (isViewOnly.value) {
                    throw new Error("Readonly");
                }
                const postData = {
                    formID: formId.value,
                    formName: state.formData.value!.formName,
                    formVersion: state.formData.value!.formVersion,
                    testMode: state.testMode.value,
                    asAdmin: state.adminReview.value,
                    segment: segment,
                    assign: assign,
                    unlock: unlock,
                    noWrite: noWrite,
                    type: type,
                    target: target,
                    targetPath: targetPath,
                    targetPathArray: targetPathArray,
                    data: JSON.stringify(data),
                };
                const mainStore = useMainStore();
                const url = await mainStore.getUrl("invokeDraft", {
                    areaCode: formTenantCode.value,
                    formId: postData.formID,
                });
                const response = await fetchXsrf(mainStore, url, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    credentials: "same-origin",
                    body: JSON.stringify(postData),
                });
                if (response.status == 200) {
                    const responseData = await response.json();
                    const formData = responseData.Form;
                    await _formPageDataUpdated({ data: formData, page: null, childIndex: null });
                    return responseData.Result;
                } else if (isOfflineAndUsingServiceWorker(response)) {
                    // ???
                } else {
                    throw new Error("Not 200 OK: " + response.status + " " + response.statusText);
                }
            })(),
        );
    };
    const saveFormPage = ({ page, isSaveAndExit, isSubmit, triggerKey }: { page: number | null; isSaveAndExit?: boolean; isSubmit?: boolean; triggerKey?: string | null }): Promise<null | SaveAllAndSubmitResponse | FormJson> => {
        const mainStore = useMainStore();
        mainStore.hasSession = true;
        // TODO rename
        return _trackAction(
            (async () => {
                if (state.adminPreview.value) {
                    throw new Error("Admin preview");
                }
                if (isViewOnly.value || (page != null && page >= 0 && isPageViewOnly(page))) {
                    throw new Error("Readonly");
                }
                if (page != null && page >= 0 && !isPageEditable(page) && (await mainStore.isFeatureFlagActiveAsync(KnownFeatureFlags.SaveOnlyEditable.Id))) {
                    throw new Error("NotEditable");
                }
                const form = state.formData.value!;
                let formDataI;
                const pagesI = pages.value;
                let childIndex = null;
                if (page != null && !isSaveAndExit) {
                    childIndex = form.children.indexOf(pagesI[page - 1]);
                    formDataI = form.getData(true);
                    if (page >= 0) {
                        const pageObj = pagesI[page - 1];
                        if (pageObj.status == "Saving") {
                            return;
                        }
                        if (isPageObjViewOnly(pageObj, false)) {
                            return;
                        }
                        if (!visiblePages.value.includes(pageObj)) {
                            return;
                        }
                        await _setPageStatus({ page, status: "Saving" });
                        formDataI.children = [];
                        formDataI.children[childIndex] = form.children[childIndex].getData()!;
                    } else {
                        if (form.children.some(p => (p as PageDef).status == "Saving")) {
                            return;
                        }
                        childIndex = -1;
                        formDataI.children = [];
                    }
                } else {
                    if (form.children.some(p => (p as PageDef).status == "Saving")) {
                        return;
                    }
                    const visiblePagesI = visiblePages.value;
                    formDataI = form.getData(true);
                    formDataI.children = [];
                    for (let index = 0; index < pagesI.length; index++) {
                        const pageObj = pagesI[index];
                        if (visiblePagesI.includes(pageObj) && !isPageObjViewOnly(pageObj, false) && (isPageObjEditable(pageObj) || !(await mainStore.isFeatureFlagActiveAsync(KnownFeatureFlags.SaveOnlyEditable.Id))) && (!isSaveAndExit || pageObj.status != "NotStarted" || index + 1 == page)) {
                            await _setPageStatus({ page: index + 1, status: "Saving" });
                            const saveChildIndex = form.children.indexOf(pageObj);
                            formDataI.children[saveChildIndex] = pageObj.getData();
                            //} else {
                            //    throw new Error(JSON.stringify([visiblePages.includes(pageObj) , '&&', !isPageObjViewOnly.value(pageObj) , '&&(', !isSaveAndExit , '||', pageObj.status != "NotStarted" , '||', index + 1 == page, ')']));
                        }
                    }
                    for (let index = 0; index < form.children.length; index++) {
                        const child = form.children[index] as CaptchaControlDef;
                        if (child.isSegment && child.isSegment(state.segment.value)) {
                            formDataI.children[index] = child.getData();
                        }
                    }
                    if (isSaveAndExit) {
                        page = null;
                    }
                }
                let formId = form.formId;
                if (formId == null) {
                    if (state.unsavedFormId.value == null) {
                        state.unsavedFormId.value = generateGuid();
                    }
                    formId = state.unsavedFormId.value;
                }
                const postData = {
                    FormModel: formDataI,
                    formHash: formDataI!.formHash,
                    formID: formId,
                    formName: formDataI!.formName,
                    formVersion: formDataI!.formVersion,
                    testMode: state.testMode.value,
                    index: childIndex,
                    asAdmin: state.adminReview.value,
                    sourceStatus: form.status,
                    segment: state.segment.value,
                    submitType: triggerKey,
                };
                const url = await mainStore.getUrl(isSubmit ? "saveFullAndSubmitDraft" : "saveDraft" + (page != null ? "Page" : "Full"), {
                    formId: formId,
                    formName: form.formName,
                    formVersion: form.formVersion,
                    formHash: form.formHash,
                    testMode: state.testMode.value,
                    page: childIndex,
                    areaCode: formTenantCode.value,
                    asAdmin: postData.asAdmin,
                    sourceStatus: postData.sourceStatus,
                    segment: postData.segment,
                    submitType: postData.submitType,
                });
                let responseData;
                try {
                    const response = await fetchXsrf(mainStore, url, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        credentials: "same-origin",
                        body: JSON.stringify(postData),
                    });
                    throwIfNonSuccessStatus(response);
                    responseData = await response.json();
                } catch (e) {
                    if (page != null && page > 0) {
                        await _setPageStatus({ page, status: "Invalid" });
                    } else {
                        _setAllPagesStatus("Invalid");
                    }
                    return null;
                }
                const formDataR = isSubmit ? responseData.Form : responseData;
                await _formPageDataUpdated({ data: formDataR, page, childIndex });
                if (form.formId == null) {
                    _setFormId(formId);
                }
                return responseData;
            })(),
        );
    };

    const deleteDraft = async ({ asAdmin }: { asAdmin: boolean }) => {
        return _trackAction(
            (async () => {
                if (state.adminPreview.value) {
                    throw new Error("Admin preview");
                }
                if (!asAdmin) {
                    if (state.adminReview.value) {
                        throw new Error("Admin review");
                    }
                }
                if (isViewOnly.value) {
                    throw new Error("Readonly");
                }
                const id = state.formData.value!.formId;
                if (id == null) {
                    return;
                }
                const mainStore = useMainStore();
                await deleteDraftFunc(mainStore, id, asAdmin, formTenantCode.value!);
            })(),
        );
    };
    const returnDraft = async ({ segment, comment }: { segment: string; comment: string }): Promise<{ success: boolean; messages: string[] }> => {
        const result = await invokeForm({ type: "ReturnTo", target: segment, segment, data: { Comment: comment } });
        return { success: result.Success, messages: result.FailureMessages };
    };
    const resendEmail = async ({ segment, comment, eventType }: { segment: string; comment: string; eventType: string }): Promise<{ success: boolean; messages: string[] }> => {
        const result = await invokeForm({ type: "Resend" + eventType, target: segment, segment: eventType == "Notification" ? null : segment, data: { Comment: comment } });
        return { success: result.Success, messages: result.FailureMessages };
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const workflowManualOverride = async ({ type, data }: { type: string; data: any }): Promise<{ success: boolean; messages: string[] }> => {
        const workflowEl = state.formData.value!.getElementsByFilter(i => i.definition.type == "WorkflowDef");
        const result = await invokeForm({ type, target: workflowEl[0].id!, segment: null, data });
        return { success: result.Success, messages: result.FailureMessages };
    };
    const loadFormData = async ({ formId, areaUrlSlug, areaCode, formHash, formName, formVersion, testMode, urlSlug, externalData }: { formId?: string; areaUrlSlug?: string; areaCode?: string; formHash?: string; formName?: string; formVersion?: string; testMode?: boolean; urlSlug?: string; externalData?: Record<string, string[]> }): Promise<void> => {
        _formDataIsLoading();
        const mainStore = useMainStore();
        const fetchPageDef = (areaCode: string | null | undefined, areaUrlSlug: string | null | undefined, formName: string, formVersion: string, formHash: string) =>
            mainStore
                .getUrl("getPageDefinitionData", { areaCode: areaCode, areaUrlSlug: areaUrlSlug, formName, formVersion, formHash, testMode })
                .then(url => fetch(url, { credentials: "same-origin" }))
                .then(r => r.json() as Promise<FormDefinitionFormItem>);
        const draftPageUrl = formId == null ? await mainStore.getUrl("getDraftTemplateData", { areaCode: areaCode, areaUrlSlug: areaUrlSlug, formName, formVersion, formHash, urlSlug, testMode, asAdmin: false }) : await mainStore.getUrl("getDraftData", { areaCode: areaCode, areaUrlSlug: areaUrlSlug, formId });
        const pagePromise = fetch(draftPageUrl, { credentials: "same-origin" }); //TODO - fetch query parameter?
        const pageDefPromise = formName != null && formVersion != null && formHash != null ? fetchPageDef(areaCode, areaUrlSlug, formName, formVersion, formHash) : Promise.resolve(null);
        const pageResponse = await pagePromise;
        if (pageResponse.status == 404) {
            throw new Error("NotFound");
        }
        const pageJson = (await pageResponse.json()) as FormJson;
        let pageDefJson = null;
        try {
            pageDefJson = await pageDefPromise;
        } catch {
            // Offline mode?
        }
        if (pageDefJson == null || pageJson.formHash != pageDefJson.formHash) {
            pageDefJson = await fetchPageDef(pageJson.formTenantCode, null, pageJson.formName, pageJson.formVersion, pageJson.formHash);
        }

        await _loadFormData({
            definition: pageDefJson,
            page: pageJson,
            canAddComments: false,
            canChangeStatus: false,
            anonymousUser: false,
            canDeleteSubmissions: false,
            canDeleteReviewComments: false,
            canReplyToComments: true, // TODO?
            canAdminEditData: false, // TODO?
            canUpgradeDefinition: false, // TODO?
            adminPreview: false,
            adminReview: false,
            externalData,
            testMode: pageJson.testMode != null ? pageJson.testMode : (testMode as unknown as string) == "true", // TODO?
        });
        if (formId != null) {
            mainStore.hasSession = true;
        }
    };
    const loadAdminPreviewFormData = async ({ definition, form, baseDefinition, baseForm }: { definition: FormDefinitionFormItem; form: FormJson; baseDefinition?: FormDefinitionFormItem | null; baseForm?: FormJson | null }): Promise<void> => {
        await _loadFormData({
            definition,
            page: form,
            baseDefinition,
            canAddComments: false,
            canChangeStatus: false,
            canReplyToComments: false,
            canAdminEditData: false,
            canUpgradeDefinition: false, // TODO?
            anonymousUser: false,
            canDeleteSubmissions: false,
            canDeleteReviewComments: false,
            basePage: baseForm,
            adminPreview: true,
            adminReview: false,
            testMode: true,
        });
    };
    const loadAdminReviewFormData = async (data: { areaCode?: string; areaUrlSlug?: string; formId?: string; formHash?: string; formName?: string; formVersion?: string; testMode?: boolean; urlSlug?: string; preloadedData?: ReviewDataData }): Promise<void> => {
        const { areaCode, areaUrlSlug, formId, formHash, formName, formVersion, testMode, urlSlug, preloadedData } = data; // TODO?
        _formDataIsLoading();
        const mainStore = useMainStore();
        const fetchPageDef = (formName: string, formVersion: string, formHash: string) =>
            mainStore
                .getUrl("getPageDefinitionData", { areaCode: areaCode, areaUrlSlug: areaUrlSlug, formName, formVersion, formHash, testMode })
                .then(url => fetch(url, { credentials: "same-origin" }))
                .then(r => r.json());

        const draftPageUrl = formId == null ? await mainStore.getUrl("getDraftTemplateData", { areaCode: areaCode, areaUrlSlug: areaUrlSlug, formName, formVersion, formHash, urlSlug, forceFresh: true, asAdmin: true, testMode: testMode }) : await mainStore.getUrl("adminSearchReviewData", { tenantCode: areaCode, tenantUrlSlug: areaUrlSlug, formId });
        const pagePromise = preloadedData != null && preloadedData.form?.formId != null && preloadedData.form?.formId == formId ? Promise.resolve(preloadedData) : fetch(draftPageUrl, { credentials: "same-origin" }).then(r => r.json() as Promise<FormJson>); //TODO - fetch query parameter?
        const pageDefPromise = formName != null && formVersion != null && formHash != null ? fetchPageDef(formName, formVersion, formHash) : Promise.resolve(null);
        const reviewData = formId == null ? ({ form: await pagePromise } as ReviewDataData) : ((await pagePromise) as ReviewDataData);
        const pageJson = reviewData.form;
        let pageDefJson = await pageDefPromise;
        if (pageDefJson == null || pageJson.formHash != pageDefJson.formHash) {
            pageDefJson = await fetchPageDef(pageJson.formName, pageJson.formVersion, pageJson.formHash);
        }
        await _loadFormData({
            definition: pageDefJson,
            canAddComments: reviewData.canAddComments,
            canChangeStatus: reviewData.canChangeStatus,
            canReplyToComments: reviewData.canReplyToComments,
            canAdminEditData: reviewData.canAdminEditData,
            anonymousUser: reviewData.anonymousUser,
            canDeleteSubmissions: reviewData.canDeleteSubmissions,
            canDeleteReviewComments: reviewData.canDeleteReviewComments,
            canUpgradeDefinition: reviewData.canUpgradeDefinition,
            page: pageJson,
            adminPreview: false,
            adminReview: true,
            testMode: pageJson.testMode != null ? pageJson.testMode : (testMode as unknown as string) == "true", // TODO?
        });
    };
    const getWorkflowManualOverrideGetPossibleUpgradeRevisions = async () => {
        const workflowEl = state.formData.value!.getElementsByFilter(i => i.definition.type == "WorkflowDef");
        const result = (await invokeForm({ type: "ListPossibleDefinitionUpgradeRevisions", target: workflowEl[0].id!, segment: null, data: null })) as ManualOverrideInvokeResult;
        return { success: result.Success, messages: result.FailureMessages, revisions: result.Revisions };
    };
    const getSchemaLabelMapping = async () => {
        if (state.schemaLabelMappingPromise.value == null) {
            _setSchemaLabelMapping(
                (async () => {
                    try {
                        const mainStore = useMainStore();
                        const url = await mainStore.getUrl("schemaLabels", { tenantCode: formTenantCode.value });
                        const response = await fetchXsrf(mainStore, url, {});
                        if (response.status == 200) {
                            return (await response.json()) as Record<string, string>;
                        } else {
                            throw new Error("error: " + response.status);
                        }
                    } catch (e) {
                        _setSchemaLabelMapping(null);
                        throw e;
                    }
                })(),
            );
        }

        return state.schemaLabelMappingPromise.value;
    };
    const _loadFormData = async ({ definition, page, baseDefinition, basePage, canAddComments, canChangeStatus, canReplyToComments, canAdminEditData, anonymousUser, canDeleteSubmissions, canDeleteReviewComments, canUpgradeDefinition, adminPreview, adminReview, testMode, externalData }: { definition: FormDefinitionFormItem; page: FormJson; baseDefinition?: FormDefinitionFormItem | null; basePage?: FormJson | null; canAddComments: boolean; canChangeStatus: boolean; canReplyToComments: boolean; canAdminEditData: boolean; canUpgradeDefinition: boolean; anonymousUser: boolean; canDeleteSubmissions: boolean; canDeleteReviewComments: boolean; adminPreview: boolean; adminReview: boolean; testMode: boolean; externalData?: Record<string, string[]> }): Promise<void> => {
        const mainStore = useMainStore();
        state.visibleFlag.value = await mainStore.isFeatureFlagActiveAsync(KnownFeatureFlags.VisibilityModeIsVisibility.Id); // TODO?
        const bulkInvoke = await mainStore.isFeatureFlagActiveAsync(KnownFeatureFlags.BulkInvoke.Id);
        const nonLiveTextboxes = await mainStore.isFeatureFlagActiveAsync(KnownFeatureFlags.NonLiveTextboxes.Id);
        const buildRoot = (definition: FormDefinitionFormItem, page: FormJson) => {
            const calculationContext: CalculationContext = {
                fetchXsrf: fetchXsrf.bind(null, mainStore),
                urlFetcher: (key: string, data: Record<string, string>) => mainStore.getUrl(key, { tenantCode: formTenantCode.value, ...data }),
                getOrCreateFormId: () => getOrCreateFormId(),
                rootElement: null,
                now: mainStore.now,
                timeZone: mainStore.getTimeZone(formTenantCode.value),
                adminPreview: adminPreview,
                adminReview: adminReview,
                schemaVersion: definition.formSchemaVersion!,
                testMode: testMode,
                bulkInvoke,
                nonLiveTextboxes,
            };
            const rootElement = buildElement(null, page, definition, calculationContext) as FormDef;
            rootElement.appendExternalData(externalData!);
            if (calculationContext.rootElement != rootElement) {
                debugger;
            }
            return rootElement;
        };
        const rootElement = buildRoot(definition, page);
        let baseRootElement = null;
        if (adminPreview && baseDefinition != null) {
            baseRootElement = buildRoot(baseDefinition, basePage!);
            rootElement.linkBaseElement(baseRootElement);
        }
        _formDataLoaded({
            formData: rootElement,
            baseFormData: baseRootElement,
            adminPreview: adminPreview,
            adminReview: adminReview,
            canAddComments: canAddComments,
            canChangeStatus: canChangeStatus,
            canReplyToComments: canReplyToComments,
            canAdminEditData: canAdminEditData,
            canUpgradeDefinition: canUpgradeDefinition,
            anonymousUser: anonymousUser,
            canDeleteSubmissions: canDeleteSubmissions,
            canDeleteReviewComments: canDeleteReviewComments,
            testMode: testMode,
        });
        if (segments.value?.length ?? 0 > 0) {
            const availableSegmentsI = availableSegments.value!;
            //cspell:ignore NOMATCH
            const selectedSegment = availableSegmentsI.length == 1 ? availableSegmentsI[0].id! : state.adminPreview.value && availableSegmentsI.length > 0 ? availableSegmentsI[0].id! : "NOMATCH";
            setSegment(selectedSegment);
        }
    };
    const setCommentVisibility = async ({ elementId, elementData, commentId, visibleToSubmitter }: { elementId: string; elementData: BaseElementDefBase; commentId: string; visibleToSubmitter: boolean }): Promise<void> => {
        const formIdI = formId.value;
        const formData = new FormData();
        //formData.append('formId', formId);
        formData.append("elementId", elementId);
        formData.append("commentId", commentId);
        formData.append("visibleToSubmitter", visibleToSubmitter.toString());
        const mainStore = useMainStore();
        const url = await mainStore.getUrl("setDraftCommentVisibility", { tenantCode: formTenantCode.value, formId: formIdI });
        const response = await fetchXsrf(mainStore, url, {
            method: "POST",
            credentials: "same-origin",
            body: formData,
        });
        if (response.status == 200) {
            const responseJson = (await response.json()) as SimpleResult;
            if (responseJson.Success !== true) {
                throw new Error("Error: Success is not true");
            }
        } else {
            throw new Error("error: " + response.status);
        }
        _setCommentVisibility({ elementData, commentId, visibleToSubmitter });
    };

    const addCommentAndChangeStatus = async (data: { text: string; visibleToSubmitter: boolean; status: string; elementId?: string; elementData: BaseElementDefBase; fileIds?: string[]; fileNames?: string[]; fileContentTypes?: string[]; userName: string }): Promise<AddCommentResponse | undefined | null> => {
        const { text, visibleToSubmitter, status, elementId, elementData, fileIds, fileNames, fileContentTypes, userName } = data;
        const textEmpty = text == null || /^\s*$/.test(text);
        const statusEmpty = status == null || /^\s*$/.test(status);
        const filesEmpty = fileIds == null || fileIds.length == 0;
        if (textEmpty && statusEmpty && filesEmpty) {
            return;
        }
        const statusLabelR = statusEmpty ? "N/A" : statusLabel(status);

        const returned = status == "Returned"; // TODO - enum?
        const rejected = status == "Rejected"; // TODO - enum?
        const deleted = status == "Deleted"; // TODO - enum?
        const specialStatus = returned || rejected || deleted;
        if (returned && (textEmpty || !visibleToSubmitter)) {
            alert("You must supply a visible comment if returning a submission.");
            return;
        } else if (rejected && textEmpty) {
            alert("You must supply a comment if rejecting a submission.");
            return;
        }
        if (!specialStatus || confirm("Are you sure you wish to set this submission to " + statusLabelR + "?" + (returned ? " This will prompt the submitter to correct and resubmit this submission. You will not be able to add comments or change the status again until the submitter re-submits the submission." : deleted ? " This will PERMANENTLY delete this submission!" : ""))) {
            const formIdI = formId.value;
            const formDataI = new FormData();
            //formDataI.append('formId', formId);
            formDataI.append("elementId", elementId ?? "");
            formDataI.append("text", text == null ? "" : text);
            formDataI.append("visibleToSubmitter", visibleToSubmitter?.toString());
            formDataI.append("json", true.toString());
            formDataI.append("asAdmin", (state.adminReview.value ?? false).toString());
            formDataI.append("status", status == null ? "" : status);
            formDataI.append("userName", userName == null ? "Anonymous" : userName);
            for (const i of fileIds ?? []) {
                formDataI.append("fileIds", i);
            }
            for (const i of fileNames ?? []) {
                formDataI.append("fileNames", i);
            }
            for (const i of fileContentTypes ?? []) {
                formDataI.append("fileContentTypes", i);
            }
            const mainStore = useMainStore();
            const url = await mainStore.getUrl("addDraftComment", { tenantCode: formTenantCode.value, formId: formIdI });
            const response = await fetchXsrf(mainStore, url, {
                method: "POST",
                credentials: "same-origin",
                body: formDataI,
            });
            let responseJson = null;
            if (response.status == 200) {
                responseJson = (await response.json()) as AddCommentResponse;
                if (responseJson.success !== true) {
                    throw new Error("error: success is not true");
                } else if (!responseJson.accessible) {
                    // TODO?
                } else if (!responseJson.editable) {
                    _makeUneditable();
                }
            } else if (isOfflineAndUsingServiceWorker(response)) {
                // assume ok?
            } else {
                throw new Error("error: " + response.status);
            }

            if (!textEmpty || !filesEmpty) {
                const fileUrl = (fileId: string) => mainStore.getUrl("downloadCommentExternalReference", { tenantCode: formTenantCode.value, formId: formId.value, fileId: elementId || state.formData.value!.id, valueId: fileId });
                _addComment({ elementData, comment: { id: responseJson!.id, text, visibleToSubmitter, date: useMainStore().now(), files: await Promise.all((fileNames ?? []).map(async (i, j) => ({ filename: i, valueId: (fileIds ?? [])[j], fileUrl: await fileUrl((fileIds ?? [])[j]) }))), userName } as unknown as Comment });
            }
            if (!statusEmpty) {
                _addComment({ elementData, comment: { text: "Changed status to " + statusLabelR, date: useMainStore().now() } as Comment });
            }

            return responseJson;
        }
    };

    return {
        segment: state.segment,
        page: state.page,
        formData: state.formData,
        baseFormData: state.baseFormData,
        formDataLoading: state.formDataLoading,
        adminPreview: state.adminPreview,
        adminReview: state.adminReview,
        testMode: state.testMode,
        unsavedFormId: state.unsavedFormId,
        adminEditElementIds: state.adminEditElementIds,
        canAddComments: state.canAddComments,
        canChangeStatus: state.canChangeStatus,
        canUpgradeDefinition: state.canUpgradeDefinition,
        canReplyToComments: state.canReplyToComments,
        canAdminEditData: state.canAdminEditData,
        anonymousUser: state.anonymousUser,
        canDeleteSubmissions: state.canDeleteSubmissions,
        canDeleteReviewComments: state.canDeleteReviewComments,
        showAdminDiff: state.showAdminDiff,
        //eslint-disable-next-line @typescript-eslint/no-explicit-any
        pendingActions: state.pendingActions,
        isSaveDisabledOnSectionChange: state.isSaveDisabledOnSectionChange,
        schemaLabelMappingPromise: state.schemaLabelMappingPromise,
        fakeFormId: state.fakeFormId,
        pendingAddressVerifications: state.pendingAddressVerifications,

        getElementByVmId,
        getElementsById$,
        formDataTyped,
        baseFormDataTyped,
        getValueByVmId,
        getChildrenByVmId,
        isAdminEditingById,
        getValuesById$,
        getElementPathString,
        viewTypes,
        directLinkTemplates,
        allFileElements,
        allElementsWithFileComments,
        allElementsWithHistory,
        allHistoryEntries,
        allElementsWithIssues,
        allIssuesEntries,
        segments,
        initializedSegments,
        uninitializedSegments,
        visibleSegments,
        availableSegments,
        unlockableSegments,
        completedSegments,
        activeSegments,
        hasCompletedSegmentsToReturnTo,
        possibleStagesToReturn,
        showChanges,
        hasChanges,
        isSubmittable$,
        isOffline,
        endUserStatuses,
        formId,
        formTenantCode,
        status,
        title,
        offlineContent,
        mustLogin,
        formSettings,
        isViewOnly,
        isWorkflow,
        isPageViewOnly,
        isPageEditable,
        isPageObjViewOnly$,
        isPageObjControlsViewOnly$,
        isPageObjViewOnly,
        isPageObjEditable,
        hasIssues,
        pages,
        currentStatusLabel,
        statusLabel,
        isClientValidationOnly,
        possibleStatuses,
        visiblePages,
        visiblePages$,
        enabledPages$,
        enabledPagesPromise,
        status$,
        segment$,
        comments,
        pendingActionPromise,
        setAdminEditing,
        clearAdminEditing,
        setSegment,
        setValueByVmId,
        setFileStatusByVmId,
        setFileInfoByVmId,
        addForEachItem,
        removeForEachItem,
        setForEachChildren,
        toggleAdminDiff,
        _formDataIsLoading,
        _formDataLoaded,
        _formDataUpdated,
        _setFormId,
        _setAllPagesStatus,
        _addPendingAction,
        _removePendingAction,
        _addComment,
        _setCommentVisibility,
        _makeUneditable,
        _setSchemaLabelMapping,
        _trackAction,
        _formPageDataUpdated,
        _setPageStatus,
        getOrCreateFormId,
        uploadExternalFile,
        submitForm,
        selfAssignSegment,
        saveAllAdminEdits,
        invokeForm,
        saveFormPage,
        deleteDraft,
        returnDraft,
        resendEmail,
        workflowManualOverride,
        loadFormData,
        loadAdminPreviewFormData,
        loadAdminReviewFormData,
        getWorkflowManualOverrideGetPossibleUpgradeRevisions,
        getSchemaLabelMapping,
        _loadFormData,
        setCommentVisibility,
        addCommentAndChangeStatus,
    };
});

//export type pageStore = ReturnType<typeof usePageStore>;
